import React, { useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import {
  handleOtpRequest,
  searchServiceProviders,
  createEmailToken,
} from "../../api";
import { setNotification } from "../../redux/notificationSlice";
import { FORM_FIELDS, FORM_TEXTS } from "../../constants";
import { LeftBanner } from "../../layout/LeftBanner";
import { NavBar } from "../../layout/NavBar";
import { SearchFormExternal } from "../forms/SearchFormExternal";
import { SearchFormInternal } from "../forms/SearchFormInternal";
import { ConfirmationModal } from "../modals/ConfirmationModal";
import { mergeResponse } from "./helper/mergeResponse";
import { SearchVendorResult } from "./SearchVendorResult";
import { OtpModal } from "../modals/OtpModal";

const initialEmailModalState = {
  requestId: "",
  show: false,
  message: "",
  email: "",
  type: "",
  name: "",
};

export const SearchVendor = () => {
  const [emailModal, setEmailModal] = useState(initialEmailModalState);
  const [verifiedData, setVerifiedData] = useState(null);
  const [errors, setErrors] = useState({ external: false, internal: false });
  const [otpState, setOtpState] = useState({
    show: false,
    value: "",
    isVerified: false,
    loading: false,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state?.user?.profile);
  const userType = useMemo(() => (userInfo ? "internal" : "external"), [userInfo]);

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors: formErrors },
  } = useForm({ mode: "onBlur" });

  const validateInternalData = useCallback((data) => {
    return !(
      data.servicesProvided === undefined &&
      data.serviceCountries === undefined &&
      data.serviceProviderName === ""
    );
  }, []);

  const createRequestData = useCallback(
    (data) => ({
      operation: "searchServiceProvider",
      userType,
      request: {
        ...data,
        servicesProvided: data?.servicesProvided?.map((item) => item.value),
        serviceCountries: data?.serviceCountries?.map((item) => item.value),
      },
    }),
    [userType]
  );

  const sendOtp = useCallback(async (request, emailId) => {
    const otpRequestData = {
      operation: "generateOtp",
      request: {
        requestId: request.requestId,
        serviceProviderName: request.serviceProviderName,
        email: emailId,
      },
    };
    await handleOtpRequest(otpRequestData);
  }, []);

  const showEmailModal = useCallback((emailType, response) => {
    const emailField = emailType === "primary" ? "primaryEmail" : "secondaryEmail";
    setEmailModal({
      requestId: response?.requestId,
      show: true,
      message: `Please verify your ${emailType} email address to update service provider`,
      email: response?.[emailField],
      type: emailType,
      name: response?.primaryName,
    });
  }, []);

  const handleSearchResponse = useCallback(
    async (response, data) => {
      const res = response?.[0];
      if (!res) return;
      const { primaryEmail, secondaryEmail } = data;
      const isPrimaryVerified = res?.isPrimaryEmailVerified;
      const isSecondaryVerified = res?.isSecondaryEmailVerified;
      const hasPrimary = Boolean(primaryEmail);
      const hasSecondary = Boolean(secondaryEmail);

      const sendOtpOrShowModal = async (email, type, isVerified) => {
        if (isVerified) {
          setOtpState((s) => ({ ...s, show: true }));
          await sendOtp(data, email);
        } else {
          showEmailModal(type, res);
        }
      };
      if (hasPrimary && hasSecondary) {
        if (isPrimaryVerified && isSecondaryVerified) {
          await sendOtpOrShowModal(primaryEmail, "primary", true);
          return;
        }
        if (!isPrimaryVerified) {
          showEmailModal("primary", res);
          return;
        }
        await sendOtpOrShowModal(primaryEmail, "primary", true);
        return;
      }
      if (hasPrimary) {
        await sendOtpOrShowModal(primaryEmail, "primary", isPrimaryVerified);
        return;
      }
      if (hasSecondary) {
        await sendOtpOrShowModal(secondaryEmail, "secondary", isSecondaryVerified);
      }
    },
    [sendOtp, showEmailModal]
  );

  const submitForm = useCallback(
    async (data) => {
      if (userType === "internal") {
        if (!validateInternalData(data)) {
          setErrors({ internal: true, external: false });
          return;
        }
        setErrors({ internal: false, external: false });
      } else {
        if (!data.primaryEmail && !data.secondaryEmail) {
          setErrors({ internal: false, external: true });
          return;
        }
        setErrors({ internal: false, external: false });
      }

      const requestData = createRequestData(data);
      const response = await searchServiceProviders(requestData);

      if (response?.responseCode === 404 || response?.length === 0) {
        dispatch(
          setNotification({
            type: "warning",
            show: true,
            message: "Warning! Invalid input. Please change your search criteria.",
          })
        );
        return;
      }
      if(response?.responseCode === 400){
        dispatch(
          setNotification({
            type: "error",
            show: true,
            message: "Error! There is some issue searching the service provider. Please try again later.",
          })
        );
        return;
      }

      const responseData = response?.responseData;

      if (userType === "internal") {
        setVerifiedData(mergeResponse(responseData));
      } else {
        if (responseData?.[0]) {
          setVerifiedData(responseData[0]);
        }
        await handleSearchResponse(responseData, data);
      }
    },
    [createRequestData, dispatch, handleSearchResponse, userType, validateInternalData]
  );

  const closeOtpModal = useCallback(() => {
    setOtpState({ show: false, value: "", isVerified: false, loading: false });
  }, []);

  const handleOtpChange = useCallback(
    async (e) => {
      const value = e.target.value;
      setOtpState((s) => ({ ...s, value, loading: false }));

      if (value.length === 6) {
        const formdata = watch();
        const requestData = {
          operation: "verifyOtp",
          request: {
            serviceProviderName: formdata.serviceProviderName,
            requestId: formdata.requestId,
            otp: value,
          },
        };

        const otpResp = await handleOtpRequest(requestData);
        setOtpState((s) => ({
          ...s,
          loading: true,
          isVerified: otpResp?.responseCode === 200,
        }));
      }
    },
    [watch]
  );

  const handleSearchWithOtp = useCallback(async () => {
    if (otpState.isVerified) {
      const requestData = createRequestData(watch());
      const response = await searchServiceProviders(requestData);
      if (response?.responseCode === 200) {
        setVerifiedData(response?.responseData[0]);
        setOtpState((s) => ({ ...s, show: false }));
        navigate("/update-serviceprovider", { state: response?.responseData });
      }
    }
  }, [createRequestData, navigate, otpState.isVerified, watch]);

  const verifyEmail = useCallback(
    async (emailObj) => {
      setEmailModal(initialEmailModalState);
      const serviceProviderData = {
        operation: "createEmailToken",
        action: "verify",
        request: {
          requestId: emailObj.requestId,
          email: emailObj.email,
          type: emailObj.type,
          name: emailObj.name,
        },
      };
      const resp = await createEmailToken(serviceProviderData);
      if (resp?.responseData?.token) {
        dispatch(
          setNotification({
            type: "success",
            show: true,
            message: `Verification link sent to your ${emailObj.type} email id. Open that link to verify your email`,
          })
        );
      } else {
        dispatch(
          setNotification({
            type: "warning",
            show: true,
            message: `Unexpected server response. Please try again in sometime`,
          })
        );
      }
    },
    [dispatch]
  );

  return (
    <>
      <NavBar />
      <div className="flex mr-4">
        <div className="flex-none w-64">
          <LeftBanner />
        </div>
        <div className="flex-1 pr-24">
          <div className="w-full">
            <h3 className="font-normal text-2xl text-teal-600">
              {userInfo?.role ? FORM_TEXTS.SEARCH_RECORD : FORM_TEXTS.UPDATE_RECORD}
            </h3>
            <form className="my-5" onSubmit={handleSubmit(submitForm)}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 my-5">
                {userInfo?.role ? (
                  <SearchFormInternal register={register} control={control} />
                ) : (
                  <SearchFormExternal register={register} errors={formErrors} />
                )}
              </div>
              {errors.internal && (
                <span className="text-red-500 text-sm grid grid-cols-1 sm:grid-cols-2 gap-6 -mt-5">
                  Please select at lease one search criteria
                </span>
              )}
              {errors.external && (
                <span className="text-red-500 text-sm grid grid-cols-1 sm:grid-cols-2 gap-6 -my-5">
                  Either primary or secondary email is required
                </span>
              )}
              <div className="flex w-1/6 justify-between">
                <button
                  type="button"
                  className="bg-teal-600 text-white text-center py-1 text-lg px-4 font-normal my-3"
                  onClick={() => navigate("/")}
                >
                  {FORM_FIELDS.BTN_CANCEL}
                </button>
                <button
                  type="submit"
                  className="bg-sky-800 text-white text-center py-1 text-lg px-4 font-normal my-3"
                >
                  {FORM_FIELDS.BTN_SEARCH}
                </button>
              </div>
            </form>

            {!userInfo && (
              <OtpModal
                isOpen={otpState.show}
                header="Verify OTP (One time password) sent to your email address and Search"
                onClose={closeOtpModal}
                onOtpChange={handleOtpChange}
                otpValue={otpState.value}
                isOtpVerified={otpState.isVerified}
                loading={otpState.loading}
                onSearch={handleSearchWithOtp}
              />
            )}

            <div className="text-gray-500">
              Note:{" "}
                {userType === "external" ? (
                  <ul className="list-disc ml-5">
                    <li>
                      (<span className="text-red-500 font-bold text-lg">*</span>)
                      All fields are mandatory.
                    </li>
                    <li>
                      (<span className="text-red-500 font-bold text-lg">**</span>)
                      At least one of the fields is required.
                    </li>
                    <li>
                      A one-time password will be sent to your registered email
                      address. Please use the one time password to continue
                      search.
                    </li>
                  </ul>
                ) : (
                    <>(<span className="text-red-500 font-bold text-lg">**</span>)
                    At least one of the fields is required.</>
                )}
            </div>

            <ConfirmationModal
              isOpen={emailModal.show}
              onClose={() => setEmailModal(initialEmailModalState)}
              onDelete={() => verifyEmail(emailModal)}
              header="Verify Email address"
              message={emailModal.message}
              confirmBtn="Verify Now"
              cancelBtn="Verify Later"
            />

            {userType === "internal" && verifiedData && (
              <SearchVendorResult searchResult={verifiedData} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
